import { normalizeMailAddress } from '@utils/address'
import { getCurrentTenant, type TenantId } from '@utils/tenant-utils'
import type {
  Contractor,
  ContractorStatus,
  ContractorSummary,
  Course,
  Employee,
  EmployeeSummary,
  ForestCrew,
  ForestCrewStatus,
  ForestCrewSummary,
  GetContractorsParams,
  GetContractorsResponse,
  GetCoursesParams,
  GetCoursesResponse,
  GetEmployeesParams,
  GetEmployeesResponse,
  GetForestCrewsParams,
  GetForestCrewsResponse,
  MailAddressModel,
  MunicipalityModel,
  NameModel,
  PhoneNumberModel,
} from '@/types/Contractor'
import { ApiClient, ApiError } from './ApiClient'

const DEFAULT_PAGE_SIZE = 100

export interface ContractorEmployee {
  id: string
  name: NameModel
  phoneNumber?: PhoneNumberModel | null
  operableMachineTypes?: string
}

export interface ContractorForestCrew {
  id: string
  leaderName: NameModel
  phoneNumber?: PhoneNumberModel | null
}

const getPaginatedContractorResources = async <T>(url: string, tenantId: string): Promise<T[]> => {
  let continuationToken: string | undefined
  const items: T[] = []

  do {
    const response = await ApiClient.get<T[]>(url, {
      headers: {
        'x-tenant-id': tenantId,
        'x-page-size': DEFAULT_PAGE_SIZE.toString(),
        ...(continuationToken ? { 'x-continuation-token': continuationToken } : {}),
      },
    })

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText, response.data)
    }

    items.push(...response.data)

    const hasMoreResults =
      response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'
    continuationToken = hasMoreResults
      ? (response.headers.get('x-continuation-token') ?? undefined)
      : undefined
  } while (continuationToken)

  return items
}

export async function getContractors(
  params: GetContractorsParams,
): Promise<GetContractorsResponse> {
  const queryParams: Record<string, string> = {}
  if (params.searchTerm) queryParams.searchTerm = params.searchTerm
  if (params.organizationNumber) queryParams.organizationNumber = params.organizationNumber
  if (params.sort) queryParams.sort = params.sort

  const response = await ApiClient.get<ContractorSummary[]>('/contractors', {
    headers: {
      'x-tenant-id': params.tenantId,
      'x-page-size': params.pageSize?.toString() ?? '50',
      ...(params.continuationToken ? { 'x-continuation-token': params.continuationToken } : {}),
    },
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data,
    continuationToken: continuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export async function getContractor(id: string): Promise<Contractor> {
  const response = await ApiClient.get<Contractor>(`/contractors/${id}`)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

const normalizeOrganizationNumber = (organizationNumber: string): string =>
  organizationNumber.replace(/\D/g, '')

export async function findContractorByOrganizationNumber(
  tenantId: TenantId,
  organizationNumber: string,
): Promise<ContractorSummary | null> {
  const normalizedOrganizationNumber = normalizeOrganizationNumber(organizationNumber)
  const response = await getContractors({
    tenantId,
    organizationNumber: normalizedOrganizationNumber,
    pageSize: 1,
  })

  return response.items.at(0) ?? null
}

export interface ContractorUpdatePayload {
  remark?: string | null
  name?: { firstName: string; lastName?: string | null }
  bankAccountNumber?: { value: string } | null
  contactInformation?: {
    email?: string | null
    phoneNumber?: { countryCode: string; number: string } | null
    address?: MailAddressModel
  }
  office?: string | null
  status?: ContractorStatus
}

function createContractorUpdateBody(contractor: Contractor, updates: ContractorUpdatePayload) {
  const contactInformation = updates.contactInformation ?? contractor.contactInformation

  return {
    tenantId: contractor.tenantId,
    name: updates.name ?? contractor.name,
    organizationNumber: contractor.organizationNumber,
    remark: updates.remark !== undefined ? updates.remark : contractor.remark,
    contactInformation: contactInformation
      ? {
          ...contactInformation,
          address: normalizeMailAddress(contactInformation.address),
        }
      : undefined,
    bankAccountNumber:
      updates.bankAccountNumber !== undefined
        ? updates.bankAccountNumber
        : contractor.bankAccountNumber,
    office: updates.office !== undefined ? updates.office : contractor.office,
    status: updates.status ?? contractor.status,
  }
}

export async function updateContractor(
  contractor: Contractor,
  updates: ContractorUpdatePayload,
): Promise<Contractor> {
  const updateBody = createContractorUpdateBody(contractor, updates)

  const response = await ApiClient.put<Contractor>(`/contractors/${contractor.id}`, updateBody, {
    headers: contractor.etag ? { 'If-Match': contractor.etag } : undefined,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function updateContractorRemark(
  contractor: Contractor,
  remark: string,
): Promise<Contractor> {
  return updateContractor(contractor, { remark })
}

export interface CreateContractorBody {
  tenantId: 1 | 2
  name: { firstName: string; lastName?: string }
  organizationNumber?: { value: string }
  remark?: string
  contactInformation?: {
    phoneNumber?: { countryCode: string; number: string }
    email?: string
    address?: {
      address: {
        streetName: string
        houseNumber: string
        postalCode: string
        city: string
        country: string
      }
    }
  }
  bankAccountNumber?: { value: string }
  office: string
  status?: 0 | 1
}

export async function createContractor(body: CreateContractorBody): Promise<Contractor> {
  const response = await ApiClient.post<Contractor>('/contractors', body)
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  const etag = response.headers.get('etag') ?? undefined
  return { ...response.data, etag }
}

export async function getContractorEmployees(
  contractorId: string,
  tenantId: string,
): Promise<ContractorEmployee[]> {
  return getPaginatedContractorResources<ContractorEmployee>(
    `/contractors/${contractorId}/employees`,
    tenantId,
  )
}

export async function getContractorForestCrews(
  contractorId: string,
  tenantId: string,
): Promise<ContractorForestCrew[]> {
  return getPaginatedContractorResources<ContractorForestCrew>(
    `/contractors/${contractorId}/forest-crews`,
    tenantId,
  )
}

export async function getCourses(params: GetCoursesParams): Promise<GetCoursesResponse> {
  const { contractorId, continuationToken, pageSize = 100, tenantId } = params

  const response = await ApiClient.get<Course[]>(`/contractors/${contractorId}/courses`, {
    headers: {
      'x-page-size': pageSize.toString(),
      ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
      ...(continuationToken ? { 'x-continuation-token': continuationToken } : {}),
    },
    params: undefined,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const nextContinuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data,
    continuationToken: nextContinuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export interface CreateCourseBody {
  name: string
  description: string
}

export type UpdateCourseBody = CreateCourseBody

export async function createCourse(
  contractorId: string,
  tenantId: TenantId,
  body: CreateCourseBody,
): Promise<Course> {
  const response = await ApiClient.post<Course>(`/contractors/${contractorId}/courses`, body, {
    headers: {
      'x-tenant-id': tenantId,
    },
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function updateCourse(
  contractorId: string,
  courseId: string,
  tenantId: TenantId,
  body: UpdateCourseBody,
): Promise<Course> {
  const response = await ApiClient.put<Course>(
    `/contractors/${contractorId}/courses/${courseId}`,
    body,
    {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function deleteCourse(
  contractorId: string,
  courseId: string,
  tenantId: TenantId,
): Promise<void> {
  const response = await ApiClient.delete<void>(
    `/contractors/${contractorId}/courses/${courseId}`,
    {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}

export interface CreateEmployeeBody {
  name: NameModel
  email: string
  phoneNumber: PhoneNumberModel
  operableMachineTypes: string
  courses: string[]
  remark?: string | null
}

export type UpdateEmployeeBody = CreateEmployeeBody

export async function createEmployee(
  contractorId: string,
  tenantId: TenantId,
  body: CreateEmployeeBody,
): Promise<EmployeeSummary> {
  const response = await ApiClient.post<EmployeeSummary>(
    `/contractors/${contractorId}/employees`,
    body,
    {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function updateEmployee(
  contractorId: string,
  employeeId: string,
  tenantId: TenantId,
  body: UpdateEmployeeBody,
  etag?: string,
): Promise<Employee> {
  const response = await ApiClient.put<Employee>(
    `/contractors/${contractorId}/employees/${employeeId}`,
    body,
    {
      headers: {
        'x-tenant-id': tenantId,
        ...(etag ? { 'If-Match': etag } : {}),
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return {
    ...response.data,
    etag: response.headers.get('etag') ?? etag,
  }
}

export async function getEmployee(
  contractorId: string,
  employeeId: string,
  tenantId: TenantId,
): Promise<Employee> {
  const response = await ApiClient.get<Employee>(
    `/contractors/${contractorId}/employees/${employeeId}`,
    {
      headers: {
        'x-tenant-id': tenantId,
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function getEmployees(params: GetEmployeesParams): Promise<GetEmployeesResponse> {
  const { contractorId, tenantId, sort, continuationToken, pageSize = 10 } = params
  const queryParams: Record<string, string> = {}
  if (sort) queryParams.sort = sort

  const response = await ApiClient.get<EmployeeSummary[]>(
    `/contractors/${contractorId}/employees`,
    {
      headers: {
        ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
        'x-page-size': pageSize.toString(),
        ...(continuationToken ? { 'x-continuation-token': continuationToken } : {}),
      },
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const nextContinuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data,
    continuationToken: nextContinuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export async function updateContractorStatus(
  contractor: Contractor,
  status: ContractorStatus,
): Promise<Contractor> {
  return updateContractor(contractor, { status })
}

export async function getForestCrews(
  params: GetForestCrewsParams,
): Promise<GetForestCrewsResponse> {
  const { contractorId, sort, status, continuationToken, pageSize = 10 } = params
  const queryParams: Record<string, string> = {}
  if (sort) queryParams.sort = sort
  if (status) queryParams.status = status

  const response = await ApiClient.get<ForestCrewSummary[]>(
    `/contractors/${contractorId}/forest-crews`,
    {
      headers: {
        'x-page-size': pageSize.toString(),
        ...(continuationToken ? { 'x-continuation-token': continuationToken } : {}),
      },
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const nextContinuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data,
    continuationToken: nextContinuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export async function getForestCrew(
  contractorId: string,
  forestCrewId: string,
): Promise<ForestCrew> {
  const response = await ApiClient.get<ForestCrew>(
    `/contractors/${contractorId}/forest-crews/${forestCrewId}`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function updateForestCrewStatus(
  forestCrew: ForestCrew,
  status: ForestCrewStatus,
): Promise<ForestCrew> {
  const currentForestCrew = await getForestCrew(forestCrew.contractorId, forestCrew.id)

  const response = await ApiClient.put<ForestCrew>(
    `/contractors/${forestCrew.contractorId}/forest-crews/${forestCrew.id}`,
    {
      leaderName: currentForestCrew.leaderName,
      phoneNumber: currentForestCrew.phoneNumber,
      municipalities: currentForestCrew.municipalities,
      remark: currentForestCrew.remark,
      status,
    },
    {
      headers: currentForestCrew.etag ? { 'If-Match': currentForestCrew.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export interface CreateForestCrewBody {
  leaderName: NameModel
  phoneNumber: PhoneNumberModel
  municipalities: MunicipalityModel[]
  remark?: string | null
}

export async function createForestCrew(
  contractorId: string,
  body: CreateForestCrewBody,
): Promise<ForestCrew> {
  const response = await ApiClient.post<ForestCrew>(
    `/contractors/${contractorId}/forest-crews`,
    body,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export interface ForestCrewUpdatePayload {
  leaderName?: NameModel
  phoneNumber?: PhoneNumberModel
  municipalities?: MunicipalityModel[]
  remark?: string | null
}

const createForestCrewUpdateBody = (forestCrew: ForestCrew, updates: ForestCrewUpdatePayload) => {
  return {
    tenantId: forestCrew.tenantId,
    leaderName: updates.leaderName ?? forestCrew.leaderName,
    phoneNumber: updates.phoneNumber ?? forestCrew.phoneNumber,
    municipalities: updates.municipalities ?? forestCrew.municipalities,
    remark: updates.remark ?? forestCrew.remark,
  }
}

export async function updateForestCrew(
  forestCrew: ForestCrew,
  body: ForestCrewUpdatePayload,
): Promise<ForestCrew> {
  const updateBody = createForestCrewUpdateBody(forestCrew, body)
  const response = await ApiClient.put<ForestCrew>(
    `/contractors/${forestCrew.contractorId}/forest-crews/${forestCrew.id}`,
    updateBody,
    {
      headers: forestCrew.etag ? { 'If-Match': forestCrew.etag } : undefined,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const etag = response.headers.get('etag') ?? undefined

  return {
    ...response.data,
    etag,
  }
}

export async function deleteForestCrew(forestCrew: ForestCrew): Promise<void> {
  const response = await ApiClient.delete<void>(
    `/contractors/${forestCrew.contractorId}/forest-crews/${forestCrew.id}`,
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}

export interface GetContractorSmsHistoryParams {
  contractorId: string
  tenantId?: TenantId
  pageSize: number
  continuationToken?: string
  dateFrom?: string
  dateTo?: string
  senderName?: string
  sort?: 'createdAt_desc' | 'createdAt_asc'
}

export interface ContractorSmsHistoryEntity {
  contractorId: string
  name: string
  message: string
  phoneNumber: {
    countryCode: string
    number: string
  }
  createdBy: string
  status: string
  createdAt: string
  modifiedAt: string
}

export interface GetContractorSmsHistoryResponse {
  entities: ContractorSmsHistoryEntity[]
  continuationToken: string
  hasMoreResults: boolean
}

export async function getContractorSmsHistory(
  params: GetContractorSmsHistoryParams,
): Promise<GetContractorSmsHistoryResponse> {
  const queryParams: Record<string, string> = {}
  if (params.dateFrom) {
    queryParams.dateFrom = params.dateFrom
  }
  if (params.dateTo) {
    queryParams.dateTo = params.dateTo
  }
  if (params.senderName) {
    queryParams.senderName = params.senderName
  }
  if (params.sort) {
    queryParams.sort = params.sort
  }

  const headers: Record<string, string> = {
    'x-tenant-id': params.tenantId ?? getCurrentTenant(),
    'x-page-size': params.pageSize.toString(),
  }

  if (params.continuationToken) {
    headers['x-continuation-token'] = params.continuationToken
  }

  const response = await ApiClient.get<ContractorSmsHistoryEntity[]>(
    `/contractors/${params.contractorId}/sms`,
    {
      headers,
      params: queryParams,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    entities: response.data,
    continuationToken: continuationToken ?? '',
    hasMoreResults,
  }
}

export async function getContractorSmsSenders(
  contractorId: string,
  tenantId?: TenantId,
): Promise<string[]> {
  const response = await ApiClient.get<string[]>(`/contractors/${contractorId}/sms/senders`, {
    headers: {
      'x-tenant-id': tenantId ?? getCurrentTenant(),
    },
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}
